import { CheckCircle, Download, Error } from '@mui/icons-material';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import getProReport, { getVesselDestination } from '../../../api/pro';
import { getPorts } from '../../../api/risk-intelligence/ports';
import { useAppSelector } from '../../../hooks';
import useUserRoleAndShortId from '../../../hooks/user-details/useUserDetails';
import {
  MyFleetActionButtonGroupStatus,
  MyFleetVessel,
} from '../../../models/my-fleet.model';
import { Destination, ProVesselTypes } from '../../../models/pro.model';
import ResponseDialog from '../../../reporting/pro-report-dialog/response-dialog';
import ProReportExport from '../../../reporting/pro-report-pdf/pro-report-pdf-export';
import {
  getAnalyticsVesselType,
  getProReportAnalyticsEventLabels,
  getRoleAnalyticsEventLabel,
  getUserAnalyticsEventLabel,
  logAnalyticsEvent,
} from '../../../utils/analytics';
import DateTimeHelpers from '../../../utils/date-time-helpers.utils';
import DownloadHelpers from '../../../utils/download.utils';
import { Port } from '../../world-ports-panel/world-ports.model';
import MyFleetVesselDestinationDialog from './my-fleet-destination-dialog/my-fleet-destination-dialog';
import MyFleetProPdfDownloadButtonGroup from './my-fleet-download-pdf-button-group/my-fleet-pro-pdf-download-button-group';

enum DownloadStatus {
  Idle = 'idle',
  FetchReport = 'fetchReport',
  CreatingPDF = 'creatingPdf',
  Complete = 'complete',
  DownloadError = 'downloadError',
  DestinationError = 'destinationError',
  DestinationMatching = 'destinationMatching',
  DestinationMatched = 'destinationMatched',
  NoReportAvailable = 'noReportAvailable',
}

interface MyFleetVesselProPdfDownloadProps {
  selectedVessel: MyFleetVessel | null;
  downloadActive: boolean;
  setDownloadActive: (status: MyFleetActionButtonGroupStatus) => void;
  resetState: () => void;
}

function getDownloadIcon(status: DownloadStatus) {
  switch (status) {
    case DownloadStatus.CreatingPDF:
    case DownloadStatus.FetchReport:
    case DownloadStatus.DestinationMatching:
      return <CircularProgress size={12} />;
    case DownloadStatus.Complete:
    case DownloadStatus.DestinationMatched:
      return <CheckCircle />;
    case DownloadStatus.DownloadError:
    case DownloadStatus.DestinationError:
    case DownloadStatus.NoReportAvailable:
      return <Error />;
    default:
      return <Download />;
  }
}

function getDownloadButtonText(status: DownloadStatus) {
  switch (status) {
    case DownloadStatus.FetchReport:
      return 'Fetching Report...';
    case DownloadStatus.CreatingPDF:
      return 'Creating PDF...';
    case DownloadStatus.Complete:
      return 'Downloaded PDF';
    case DownloadStatus.DownloadError:
      return 'Download Error';
    case DownloadStatus.DestinationError:
      return 'Destination Error';
    case DownloadStatus.DestinationMatching:
      return 'Matching Destination...';
    case DownloadStatus.DestinationMatched:
      return 'Matched Destination';
    case DownloadStatus.NoReportAvailable:
      return 'No Report Available';
    default:
      return 'Download PRO Report';
  }
}

function getTooltipContent() {
  return (
    <Box>
      <Typography variant="body2">
        This will check for destination port and if it exists, create and
        download a PRO report in .pdf format for easy sharing and archiving.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {`The file will be saved to your device's default download location and can be opened in most PDF viewers.`}
      </Typography>
    </Box>
  );
}

async function fetchDestinationForVessel(
  vessel: MyFleetVessel
): Promise<Destination | null> {
  try {
    const destinationResponses = await getVesselDestination([vessel.imo]);
    const response = destinationResponses[0];

    return response?.destination || null;
  } catch {
    return null;
  }
}

function MyFleetVesselProPdfDownload({
  selectedVessel,
  downloadActive,
  setDownloadActive,
  resetState,
}: MyFleetVesselProPdfDownloadProps) {
  const [destination, setDestination] = useState<Destination | null>(null);
  const [destinationPort, setDestinationPort] = useState<Port | null>(null);
  const [status, setStatus] = useState<DownloadStatus>(DownloadStatus.Idle);
  const { ports } = useAppSelector((state) => state.ports);
  const userToken = useAppSelector((state) => state.user.idToken);
  const { flags: featureFlags } = useAppSelector((state) => state.featureFlags);
  const userDetails = useUserRoleAndShortId();
  const lastLoggedEvent = useRef<string | null>(null);

  useEffect(() => {
    setStatus(DownloadStatus.Idle);
  }, [selectedVessel]);

  const handleShowActions = () => {
    setDownloadActive(MyFleetActionButtonGroupStatus.Download);
  };

  const handleCancel = () => {
    setDownloadActive(MyFleetActionButtonGroupStatus.Idle);
    resetState();
  };

  const handleDownload = async () => {
    if (!selectedVessel) return;
    setStatus(DownloadStatus.DestinationMatching);

    const destinationResult = await fetchDestinationForVessel(selectedVessel);
    setDestination(destinationResult);

    const matchedPort = destinationResult?.matched_port;
    if (!matchedPort || !matchedPort.unlocode) {
      setStatus(DownloadStatus.DestinationError);
      return;
    }

    let currentPorts = ports;
    if (!currentPorts || currentPorts.length === 0) {
      /* Fail-over to using risk intelligence ports if no ports are loaded */
      currentPorts = await getPorts(userToken, featureFlags);
    }
    setStatus(DownloadStatus.DestinationMatched);

    const destinationPortResult: Port =
      currentPorts?.find(
        (port) =>
          port.UNLOCODE?.replace(/\s/g, '') ===
          matchedPort.unlocode?.replace(/\s/g, '')
      ) ||
      ({
        UNLOCODE: matchedPort.unlocode,
        NAME: matchedPort.name,
        COUNTRY: '',
      } as Port);
    setDestinationPort(destinationPortResult);

    const dataPromises: Promise<any>[] = [];

    const proReportPromise = getProReport(
      matchedPort.unlocode,
      selectedVessel.vessel_type === ProVesselTypes.NO_VESSEL_TYPE
        ? undefined
        : selectedVessel.vessel_type
    );
    dataPromises.push(proReportPromise);

    try {
      setStatus(DownloadStatus.FetchReport);
      const [report] = await Promise.all(dataPromises);

      setStatus(DownloadStatus.CreatingPDF);
      const now = new Date();
      const pdfFileName = `${DateTimeHelpers.dateToIso8601(now).substring(
        0,
        10
      )}-globeview-pro-report-${matchedPort?.name}-${report?.port}.pdf`;

      await DownloadHelpers.generateAndDownloadPDF(
        pdfFileName,
        <ProReportExport
          report={report}
          portName={destinationPortResult?.NAME}
          portCountry={destinationPortResult?.COUNTRY}
          portUnlocode={destinationPortResult?.UNLOCODE}
          aisRawDestination={destinationResult?.raw}
          vessel={selectedVessel}
        />
      );
      setStatus(DownloadStatus.Complete);
      // Build a unique key for this event to prevent logging multiple analytics events
      // for the same report download. This ensures that if the user downloads the same
      // report instance multiple times, only the first download is logged.
      const eventKey = `${destinationPortResult?.UNLOCODE}-${selectedVessel.imo}-${userDetails.userAnonymisedId}`;
      // Only log if this event is new
      if (lastLoggedEvent.current !== eventKey) {
        logAnalyticsEvent('MyFleet', 'ProReportPDFDownloaded', [
          getUserAnalyticsEventLabel(userDetails),
          getRoleAnalyticsEventLabel(userDetails),
          ...getProReportAnalyticsEventLabels(
            destinationPortResult?.UNLOCODE,
            getAnalyticsVesselType(
              selectedVessel.vessel_type ?? ProVesselTypes.UNKNOWN
            )
          ),
        ]);
        lastLoggedEvent.current = eventKey;
      }
    } catch (error) {
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        (error as { status?: number }).status === 404
      ) {
        setStatus(DownloadStatus.NoReportAvailable);
      } else {
        setStatus(DownloadStatus.DownloadError);
      }
    }
  };

  return (
    <>
      <MyFleetProPdfDownloadButtonGroup
        showActionButtons={downloadActive}
        onShowActions={handleShowActions}
        onCancel={handleCancel}
        onClick={handleDownload}
        statusIcon={getDownloadIcon(status)}
        statusText={getDownloadButtonText(status)}
        tooltipContent={getTooltipContent()}
        disableDownload={
          !selectedVessel ||
          status === DownloadStatus.DestinationMatching ||
          status === DownloadStatus.CreatingPDF ||
          status === DownloadStatus.FetchReport ||
          status === DownloadStatus.DestinationMatched
        }
      />
      <MyFleetVesselDestinationDialog
        open={status === DownloadStatus.DestinationError}
        onClose={() => setStatus(DownloadStatus.Idle)}
        vessel={selectedVessel}
        destination={destination || null}
      />
      <ResponseDialog
        open={
          status === DownloadStatus.NoReportAvailable ||
          status === DownloadStatus.DownloadError
        }
        onClose={() => setStatus(DownloadStatus.Idle)}
        headerText={
          status === DownloadStatus.NoReportAvailable
            ? 'PRO Report not yet available'
            : 'PRO Report download error'
        }
        titleText={
          status === DownloadStatus.NoReportAvailable ? (
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: 700,
                gap: 1,
                m: 1,
              }}
            >
              <span>
                No PRO report available for{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: destinationPort ? 'bold' : 'normal',
                    display: 'inline',
                  }}
                >
                  {destinationPort
                    ? destinationPort.NAME
                    : "this vessel's destination"}
                </Box>{' '}
                as port is currently considered to present a low risk, ports are
                continually being assessed and updated.
              </span>
            </Typography>
          ) : (
            "An error occurred while downloading the PRO report for this vessel's destination."
          )
        }
        content={
          <>
            <Grid
              container
              spacing={1}
              sx={{ paddingLeft: 2, width: '100%', mb: 1 }}
            >
              {selectedVessel && (
                <>
                  <Grid item xs={4}>
                    <Typography>Vessel Name:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {selectedVessel ? selectedVessel.name : 'Unknown Vessel'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>IMO:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {selectedVessel ? selectedVessel.imo : 'Unknown IMO'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>Destination input:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {destination
                        ? `'${destination.raw}'`
                        : 'Destination not specified'}
                    </Typography>
                  </Grid>
                </>
              )}
              {destinationPort && (
                <>
                  <Grid item xs={4}>
                    <Typography>Port Name:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {destinationPort ? destinationPort.NAME : 'Unknown Port'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>Port Unlocode:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>
                      {destinationPort
                        ? destinationPort.UNLOCODE
                        : 'Unknown Port Code'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
            <Typography>
              {status === DownloadStatus.NoReportAvailable
                ? ''
                : `Please try again later. If the issue persists, contact support.`}
            </Typography>
          </>
        }
      />
    </>
  );
}

export default MyFleetVesselProPdfDownload;
